import EcoDesignEventsBus from 'eco-design-micro/EcoDesignEventsBus';
import { isH5HandleClient } from 'eco-design-micro/utils-base/isH5Handle';
import { useEffect, useRef, useState } from 'react';

// 监听面板高度变化
export const usePanelHeight = () => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    // 动态计算内容高度
    useEffect(() => {
        if (!contentRef.current) return;
        const observer = new ResizeObserver((e) => {
            e.forEach((item) => {
                const isH5 = isH5HandleClient();
                if (isH5) return;
                setHeight(item.contentRect.height);
            });
        });
        observer.observe(contentRef.current);
        return () => observer.disconnect();
    }, []);

    return { contentRef, height };
};

// 导航栏切换逻辑
export const useNavListSwitch = () => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const tabLineRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [lineStyle, setLineStyle] = useState({ opacity: 0, left: 0, width: 0 });
    // ⭐ 用来控制延迟触发
    const hoverTimer = useRef<NodeJS.Timeout | null>(null);

    const handleClickEnter = (index: number) => {
        if (!isH5HandleClient()) return;
        setActiveIndex(index);
        EcoDesignEventsBus.emit('ef-header-search-close');
    };

    const handleMouseEnter = (index: number, flag: boolean = false) => {
        if (isH5HandleClient()) return;
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
        }
        hoverTimer.current = setTimeout(() => {
            if (activeIndex === null) {
                // null 的时候去掉动画
                tabLineRef.current?.classList.remove('use-transition');
            } else {
                tabLineRef.current?.classList.add('use-transition');
            }
            setActiveIndex(index);
            EcoDesignEventsBus.emit('ef-header-search-close');
            const el = itemRefs.current[index];
            if (el) {
                setLineStyle({
                    opacity: 1,
                    left: el.offsetLeft,
                    width: el.offsetWidth,
                });
            }
        }, 200);
    };

    const handleMouseLeave = () => {
        if (isH5HandleClient()) return;

        setActiveIndex(null);
        EcoDesignEventsBus.emit('ef-header-search-close');
        setLineStyle((prev) => ({ ...prev, opacity: 0 }));
    };

    const handleMouseLeaveTab = () => {
        if (isH5HandleClient()) return;
        // 离开时取消 hover 延时
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    };

    // 监听移动端关闭
    useEffect(() => {
        EcoDesignEventsBus.on('global-header-nav-item1-close', () => {
            setActiveIndex(null);
        });
    }, []);

    return {
        activeIndex,
        lineStyle,
        handleClickEnter,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseLeaveTab,
        tabLineRef,
        itemRefs,
    };
};

// 监听 serach 面板打开情况
export const useSearchOpen = () => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const callback: MutationCallback = (mutations) => {
            mutations.forEach((mutation) => {
                const el = mutation.target as HTMLDivElement;
                setOpen(mutation.attributeName === 'class' && el.classList.contains('ef-header-v2-search-open'));
            });
        };
        const mutation = new MutationObserver(callback);
        // 属性监听，知道是否打开搜索框
        const el = document.querySelector(`.ef-header-v2-bar-search`);
        if (el) {
            mutation.observe(el, {
                attributes: true,
            });
            return () => mutation.disconnect();
        }
    }, []);
    return { open };
};
